import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = { reuniao: "Reunião", vencimento: "Vencimento", compromisso: "Compromisso", tarefa: "Tarefa" };
const dayKey = (iso) => iso.slice(0, 10);

export default function AgendaPage() {
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", start: "", kind: "reuniao", recurring: "", contact_phone: "" });

  const load = () => {
    const start = new Date(); start.setDate(start.getDate() - 7);
    const end = new Date(); end.setDate(end.getDate() + 45);
    api.get("/agenda", { params: { start: start.toISOString(), end: end.toISOString(), mine } }).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, [mine]);

  const create = async () => {
    if (!form.title || !form.start) { toast.error("Informe título e data"); return; }
    try {
      await api.post("/events", { ...form, start: new Date(form.start).toISOString(), recurring: form.recurring || null });
      setOpen(false); setForm({ title: "", start: "", kind: "reuniao", recurring: "", contact_phone: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
  };
  const remove = async (id) => { await api.delete(`/events/${id}`); load(); };

  const now = new Date().toISOString();
  const groups = items.reduce((acc, it) => { (acc[dayKey(it.start)] = acc[dayKey(it.start)] || []).push(it); return acc; }, {});

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="heading text-3xl font-extrabold flex items-center gap-2"><CalendarDays className="w-7 h-7 text-emerald-600" /> Agenda</h1>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setMine(!mine)} data-testid="agenda-mine-toggle">{mine ? "Minha agenda" : "Agenda da equipe"}</Button>
            <Button size="sm" onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="agenda-new-button"><Plus className="w-4 h-4 mr-1" /> Novo compromisso</Button>
          </div>
        </div>
        {Object.keys(groups).length === 0 && <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Nada agendado nos próximos 45 dias.</CardContent></Card>}
        {Object.entries(groups).map(([day, list]) => (
          <div key={day}>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
              {new Date(day + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            <Card><CardContent className="p-0">
              {list.map((it) => {
                const late = it.type === "task" ? it.status !== "done" && dayKey(it.start) < now.slice(0, 10) : it.start < now;
                return (
                  <div key={`${it.id}-${it.occurrence || 0}`} className="flex items-center gap-3 p-3 border-b border-border" data-testid="agenda-item">
                    <span className={`status-dot ${late ? "bg-rose-500" : "bg-emerald-500"}`} title={late ? "Vencido" : "Dentro do prazo"} />
                    <div className="text-xs mono w-12 text-muted-foreground">{it.type === "task" ? "—" : new Date(it.start).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{it.title}</div>
                      <div className="text-[11px] text-muted-foreground">{it.user_name || "—"}{it.contact_phone ? ` · +${it.contact_phone}` : ""}{it.recurring ? ` · ${it.recurring === "weekly" ? "semanal" : "mensal"}` : ""}</div>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">{KINDS[it.kind] || it.kind}</Badge>
                    {it.type === "event" && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(it.id)}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                );
              })}
            </CardContent></Card>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo compromisso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-field" /></div>
            <div><Label>Data e hora</Label><Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="event-start-field" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Tipo</Label>
                <select className="w-full h-10 mt-1 border border-border rounded-md bg-background px-2 text-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="reuniao">Reunião</option><option value="vencimento">Vencimento</option><option value="compromisso">Compromisso</option>
                </select></div>
              <div><Label>Repetir</Label>
                <select className="w-full h-10 mt-1 border border-border rounded-md bg-background px-2 text-sm" value={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.value })}>
                  <option value="">Não repetir</option><option value="weekly">Semanal</option><option value="monthly">Mensal</option>
                </select></div>
            </div>
            <div><Label>Telefone do cliente (opcional)</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value.replace(/\D/g, "") })} /></div>
          </div>
          <DialogFooter><Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700" data-testid="event-save-button">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
